import { useLayoutEffect, useState, type RefObject } from 'react'
import { anchoredPanelPosition } from '../utils/anchoredPanelPosition'

/** Fixed-position placement (viewport pixels) for a portaled chart panel. */
export interface PanelPosition {
  left: number
  top: number
}

/**
 * Two-pass placement for a portaled chart panel (settings modal / indicator +
 * overlay settings drawers): render hidden, measure, then position —
 *
 *  - `anchor` (an opening click) → beside it via `anchoredPanelPosition`;
 *  - else, when `fallback: 'center'` (default) → centered over `centerIn`'s rect
 *    (or the viewport centre if `centerIn` is absent), viewport-clamped;
 *  - else (`fallback: 'none'`) → `null`, so the host applies its own default.
 *
 * Re-measured on window resize and (when `centerIn` is given) when that element
 * resizes. Pair with `visibility: hidden` until the returned pos is non-null.
 */
export function usePanelPosition(
  ref: RefObject<HTMLElement | null>,
  opts: {
    anchor?: { x: number; y: number }
    centerIn?: RefObject<HTMLElement | null>
    /** No-anchor fallback: center over the chart/viewport (default) or defer to
     *  the host's own placement (`'none'` — the settings modal's right edge). */
    fallback?: 'center' | 'none'
  },
): PanelPosition | null {
  const { anchor, centerIn, fallback = 'center' } = opts
  const [pos, setPos] = useState<PanelPosition | null>(null)

  useLayoutEffect(() => {
    const measure = () => {
      const el = ref.current
      if (!el) return
      const pw = el.offsetWidth
      const ph = el.offsetHeight
      // Anchored (opening click) placement wins.
      if (anchor) {
        const { left, top } = anchoredPanelPosition(anchor, pw, ph)
        setPos({ left: Math.round(left), top: Math.round(top) })
        return
      }
      // No anchor + no-center fallback → host default (null).
      if (fallback === 'none') {
        setPos(null)
        return
      }
      // Center over the element (or the viewport when absent), clamped so a small
      // grid cell can't push the panel off screen.
      const vw = window.innerWidth
      const vh = window.innerHeight
      const rect = centerIn?.current?.getBoundingClientRect()
      const cx = rect && rect.width > 0 ? rect.left + rect.width / 2 : vw / 2
      const cy = rect && rect.height > 0 ? rect.top + rect.height / 2 : vh / 2
      setPos({
        left: Math.round(Math.min(Math.max(cx - pw / 2, 8), Math.max(8, vw - pw - 8))),
        top: Math.round(Math.min(Math.max(cy - ph / 2, 8), Math.max(8, vh - ph - 8))),
      })
    }
    measure()
    window.addEventListener('resize', measure)
    let ro: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined' && centerIn?.current) {
      ro = new ResizeObserver(measure)
      ro.observe(centerIn.current)
    }
    return () => {
      window.removeEventListener('resize', measure)
      ro?.disconnect()
    }
  }, [ref, anchor, centerIn, fallback])

  return pos
}
